// We have both a mobile and a desktop version of the filters. We don't want to duplicate the logic
// that figures out what's available when.
import { FeatureFlags } from '@socrata/platform/feature-flags';
import {
  Tab,
  TAB_ALL_ASSETS,
  TAB_FEDERATED,
  TAB_MY_ASSETS,
  TAB_MY_QUEUE,
} from 'common/components/AssetBrowser/lib/constants';
import { summarizeCanReviewWorkflow } from 'common/core/approvals';

export enum Filters {
  ShowPublicTargetAudience = 'showPublicTargetAudience',
  ShowInternalTargetAudience = 'showInternalTargetAudience',
  EnrolledInArchival = 'enrolledInArchival',
  OnlyRecentlyViewed = 'onlyRecentlyViewed',
  Version = 'version',
  Authority = 'authority',
  OwnedBy = 'ownedBy',
  Visibility = 'visibility',
  Source = 'source',
  Category = 'category',
  Tag = 'tag',
  Requester = 'requester',
  CustomFacet = 'customFacet',
  ApprovalStatus = 'approvalStatus', // Controlled by a prop.
  AssetTypes = 'assetTypes', // Always true.
}

export type EnabledFilters = {
  [K in Filters]: boolean;
};

const defaultFilters = (showAwaitingApprovalFilter: boolean): EnabledFilters => {
  return {
    showInternalTargetAudience: false,
    showPublicTargetAudience: false,
    enrolledInArchival: FeatureFlags.valueOrDefault('enable_asset_archival', false),
    onlyRecentlyViewed: true,
    version: true,
    authority: true,
    ownedBy: true,
    visibility: true,
    source: false,
    category: true,
    tag: true,
    customFacet: true,
    requester: false,
    approvalStatus: showAwaitingApprovalFilter,
    assetTypes: true,
  };
};

const allAssetsFilters = (showAwaitingApprovalFilter: boolean): EnabledFilters => {
  return {
    showInternalTargetAudience: false,
    showPublicTargetAudience: false,
    enrolledInArchival: FeatureFlags.valueOrDefault('enable_asset_archival', false),
    onlyRecentlyViewed: true,
    version: true,
    authority: true,
    ownedBy: true,
    visibility: true,
    source: false,
    category: true,
    tag: true,
    customFacet: true,
    requester: false,
    approvalStatus: showAwaitingApprovalFilter,
    assetTypes: true,
  };
};

const myAssetsFilters = (showAwaitingApprovalFilter: boolean): EnabledFilters => {
  return {
    showInternalTargetAudience: false,
    showPublicTargetAudience: false,
    enrolledInArchival: FeatureFlags.valueOrDefault('enable_asset_archival', false),
    onlyRecentlyViewed: true,
    version: true,
    authority: false,
    ownedBy: false,
    visibility: true,
    source: false,
    category: true,
    tag: true,
    customFacet: true,
    requester: false,
    approvalStatus: showAwaitingApprovalFilter,
    assetTypes: true,
  };
};

const federatedFilters = (showAwaitingApprovalFilter: boolean): EnabledFilters => {
  return {
    showInternalTargetAudience: false,
    showPublicTargetAudience: false,
    enrolledInArchival: FeatureFlags.valueOrDefault('enable_asset_archival', false),
    onlyRecentlyViewed: true,
    version: true,
    authority: true,
    ownedBy: true,
    visibility: true,
    source: true,
    category: true,
    tag: true,
    customFacet: true,
    requester: false,
    approvalStatus: showAwaitingApprovalFilter,
    assetTypes: true,
  };
};

const approvalsFilters = (showAwaitingApprovalFilter: boolean): EnabledFilters => {
  const intendedAudience = !!(
    FeatureFlags.valueOrDefault('strict_permissions', false) && summarizeCanReviewWorkflow().length > 1
  );

  return {
    showInternalTargetAudience: intendedAudience,
    showPublicTargetAudience: intendedAudience,
    enrolledInArchival: false,
    onlyRecentlyViewed: false,
    version: false,
    authority: false,
    ownedBy: false,
    visibility: false,
    source: false,
    category: false,
    tag: false,
    customFacet: false,
    requester: true,
    approvalStatus: showAwaitingApprovalFilter,
    assetTypes: true,
  };
};

export const getEnabledFilters = (
  activeTab: Tab,
  showAwaitingApprovalFilter: boolean = false,
): EnabledFilters => {
  if (activeTab === TAB_ALL_ASSETS) {
    return allAssetsFilters(showAwaitingApprovalFilter);
  }

  if (activeTab === TAB_MY_ASSETS) {
    return myAssetsFilters(showAwaitingApprovalFilter);
  }

  if (activeTab === TAB_FEDERATED) {
    return federatedFilters(showAwaitingApprovalFilter);
  }

  if (activeTab === TAB_MY_QUEUE) {
    return approvalsFilters(showAwaitingApprovalFilter);
  }

  return defaultFilters(showAwaitingApprovalFilter);
};
